import { useRef } from 'react';
import type { LifestyleElement, SourceFields } from '../api/types';
import {
  DEFAULT_LIFESTYLE,
  LIFESTYLE_RATIO_MAX,
  LIFESTYLE_RATIO_MIN,
  LIFESTYLE_UPLOAD,
  splitWidths,
} from '../api/types';

interface Props {
  /** undefined = the config has no lifestyle key at all (feature off). */
  value: LifestyleElement | undefined;
  sourceFields: SourceFields;
  /** Enable: write the whole element. */
  onEnable: () => void;
  /** Disable: DELETE the key rather than persisting enabled:false — a disabled
   *  element still sits in config.elements and still changes config_hash, which
   *  would re-render every product on a config that isn't using the feature. */
  onDisable: () => void;
  onChange: (patch: Partial<LifestyleElement>) => void;
  onUploadAsset: (file: File) => Promise<void>;
  uploading: boolean;
}

// The half is tall and narrow, so a photo is cropped left-to-right, not
// top-to-bottom. This picks which side of a wide shot survives.
const FOCUS_OPTIONS: Array<[LifestyleElement['focus'], string]> = [
  ['left', 'Left'],
  ['center', 'Center'],
  ['right', 'Right'],
];

export function LifestyleEditor({
  value,
  sourceFields,
  onEnable,
  onDisable,
  onChange,
  onUploadAsset,
  uploading,
}: Props) {
  const fileRef = useRef<HTMLInputElement>(null);
  const enabled = value?.enabled === true;

  if (!enabled || !value) {
    return (
      <button
        type="button"
        aria-label="Lifestyle mode"
        aria-pressed={false}
        onClick={onEnable}
        className="w-full rounded-xl border border-dashed border-ff-forest/35 px-3 py-3
                   text-left transition-colors hover:border-ff-forest hover:bg-ff-mint/40"
      >
        <SplitGlyph />
        <span className="mt-2 block text-sm text-ff-forest">
          Split the canvas with a photo
        </span>
        <span className="block text-[11px] leading-snug text-ff-mute">
          One half a lifestyle shot, the other the product.
        </span>
      </button>
    );
  }

  const ratio = value.ratio ?? 0.5;
  const photoOnLeft = value.side !== 'right';
  const pct = Math.round(ratio * 100);
  const fieldChoices = sourceFields.custom;
  const uploadMode = value.asset != null || value.field == null;
  const selectValue = uploadMode ? LIFESTYLE_UPLOAD : value.field ?? LIFESTYLE_UPLOAD;

  // Picking a field CLEARS the asset: an asset always wins in the renderer, so
  // leaving one behind would show a field in this dropdown and render the
  // uploaded image instead.
  const pickSource = (next: string) =>
    next === LIFESTYLE_UPLOAD
      ? onChange({ field: null })
      : onChange({ field: next, asset: null });

  return (
    <div className="space-y-4">
      <SplitPreview
        ratio={ratio}
        photoOnLeft={photoOnLeft}
        pct={pct}
        onRatio={(r) => onChange({ ratio: r })}
      />

      <div role="radiogroup" aria-label="Lifestyle side" className="flex gap-1.5">
        {(['left', 'right'] as const).map((s) => (
          <button
            key={s}
            type="button"
            role="radio"
            aria-checked={value.side === s}
            aria-label={s === 'left' ? 'Photo on left' : 'Photo on right'}
            onClick={() => onChange({ side: s })}
            className={`flex-1 rounded-full px-3 py-1.5 text-[11px] font-medium transition-colors ${
              value.side === s
                ? 'bg-ff-forest text-ff-ivory'
                : 'bg-ff-mint/60 text-ff-forest hover:bg-ff-mint'
            }`}
          >
            Photo {s}
          </button>
        ))}
      </div>

      <div className="space-y-1.5">
        <label
          htmlFor="ls-source"
          className="block font-mono text-[10px] uppercase tracking-widish text-ff-mute"
        >
          Photo source
        </label>
        <select
          id="ls-source"
          aria-label="Lifestyle photo source"
          value={selectValue}
          onChange={(e) => pickSource(e.target.value)}
          className="ff-input py-2 text-sm"
        >
          {!uploadMode && value.field && !fieldChoices.includes(value.field) && (
            <option value={value.field}>
              {fieldChoices.length === 0
                ? `Product field · ${value.field}`
                : `${value.field} — not mapped`}
            </option>
          )}
          {fieldChoices.map((f) => (
            <option key={f} value={f}>
              {f}
            </option>
          ))}
          {/* With nothing mapped there would otherwise be no way BACK to a
              per-product photo once upload mode is picked — a dead end. */}
          {fieldChoices.length === 0 && uploadMode && (
            <option value={DEFAULT_LIFESTYLE.field ?? 'lifestyle_image'}>
              Use a product field…
            </option>
          )}
          <option value={LIFESTYLE_UPLOAD}>Upload an image…</option>
        </select>

        {/* Constraints appear next to the control they affect, at the moment
            they apply — never as a standing paragraph. */}
        {!uploadMode && fieldChoices.length === 0 && (
          <>
            <input
              type="text"
              aria-label="Lifestyle field name"
              value={value.field ?? ''}
              onChange={(e) => onChange({ field: e.target.value, asset: null })}
              className="ff-input py-2 text-sm font-mono"
            />
            <p className="text-[11px] leading-snug text-ff-mute">
              No custom fields are mapped on this store, so this name isn't checked.
              Map it in the store's source settings to reach the renderer.
            </p>
          </>
        )}
        {!uploadMode && fieldChoices.length > 0 && (
          <p className="text-[11px] leading-snug text-ff-mute">
            Each product uses its own photo. Products with no value render full-canvas.
          </p>
        )}

        {uploadMode && (
          <div className="space-y-1.5 pt-0.5">
            {value.asset ? (
              <div className="flex items-center gap-2">
                <span className="ff-chip min-w-0 flex-1 bg-ff-mint text-ff-forest">
                  <span className="truncate font-mono normal-case tracking-normal">
                    {value.asset.split('/').pop()}
                  </span>
                </span>
                <button
                  type="button"
                  aria-label="Upload lifestyle image"
                  disabled={uploading}
                  onClick={() => fileRef.current?.click()}
                  className="ff-btn-ghost shrink-0 px-3 py-1 text-[11px]"
                >
                  {uploading ? 'Uploading…' : 'Replace'}
                </button>
              </div>
            ) : (
              <button
                type="button"
                aria-label="Upload lifestyle image"
                disabled={uploading}
                onClick={() => fileRef.current?.click()}
                className="ff-btn-ghost w-full py-2 text-[11px]"
              >
                {uploading ? 'Uploading…' : 'Choose an image'}
              </button>
            )}
            <p className="text-[11px] leading-snug text-ff-mute">
              Used on every product this config matches.
            </p>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png"
              aria-label="Lifestyle image file"
              className="hidden"
              onChange={(e) => {
                const f = e.target.files?.[0];
                if (f) void onUploadAsset(f);
                e.target.value = '';
              }}
            />
          </div>
        )}
      </div>

      <div className="space-y-1.5">
        <span className="block font-mono text-[10px] uppercase tracking-widish text-ff-mute">
          Crop anchor
        </span>
        <div role="radiogroup" aria-label="Lifestyle focus" className="flex gap-1.5">
          {FOCUS_OPTIONS.map(([f, label]) => (
            <button
              key={f}
              type="button"
              role="radio"
              aria-checked={value.focus === f}
              aria-label={label}
              onClick={() => onChange({ focus: f })}
              className={`flex-1 rounded-full px-2 py-1.5 text-[11px] font-medium transition-colors ${
                value.focus === f
                  ? 'bg-ff-forest text-ff-ivory'
                  : 'bg-ff-mint/60 text-ff-forest hover:bg-ff-mint'
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* No caveat line any more: leaving the split now restores the overlays
          it hid and the positions it clamped, so there is nothing left to warn
          about and the button can just say what it does. */}
      <div className="border-t border-ff-sand/70 pt-3">
        <button
          type="button"
          aria-label="Lifestyle mode"
          aria-pressed={true}
          onClick={onDisable}
          className="ff-btn-ghost w-full py-1.5 text-[11px]"
        >
          Go back to single layout
        </button>
      </div>
    </div>
  );
}

/** The signature control: the split itself, at a glance and to scale.
 *
 *  Replaces round 1's two radios + slider + "51% · 551px photo / 529px product"
 *  prose. Side and ratio are spatial properties, so a spatial control reads
 *  faster than any amount of text — and at a 330px rail the text version wrapped
 *  onto four lines. The range input sits invisibly on top, which buys native
 *  pointer dragging, keyboard arrows and screen-reader semantics for free.
 */
function SplitPreview({
  ratio,
  photoOnLeft,
  pct,
  onRatio,
}: {
  ratio: number;
  photoOnLeft: boolean;
  pct: number;
  onRatio: (r: number) => void;
}) {
  const [productW, lifestyleW] = splitWidths(1080, ratio);
  const photo = (
    <div
      className="relative grid place-items-center bg-ff-forest text-ff-ivory"
      style={{ width: `${(lifestyleW / 1080) * 100}%` }}
    >
      <div className="text-center leading-tight">
        <span className="block font-mono text-[9px] uppercase tracking-widish opacity-70">
          Photo
        </span>
        <span className="block font-mono text-[13px]">{pct}%</span>
      </div>
    </div>
  );
  const product = (
    <div
      className="relative bg-ff-mint"
      style={{ width: `${(productW / 1080) * 100}%` }}
    >
      {/* Ghosts of the composition, so the half reads as "the product side"
          rather than an empty box. Decorative only. */}
      <span className="absolute left-1.5 top-1.5 h-1.5 w-5 rounded-full bg-ff-forest/25" />
      <span className="absolute right-1.5 top-1.5 h-1.5 w-4 rounded-full bg-ff-accent/35" />
      <span className="absolute left-1/2 top-1/2 h-5 w-8 -translate-x-1/2 -translate-y-1/2 rounded bg-ff-forest/20" />
      <span className="absolute bottom-1.5 left-1.5 h-2 w-9 rounded bg-white/80" />
    </div>
  );

  return (
    <div className="relative">
      <div
        data-testid="split-preview"
        className="flex h-[68px] overflow-hidden rounded-xl border border-ff-sand"
      >
        {photoOnLeft ? photo : product}
        {photoOnLeft ? product : photo}
      </div>
      {/* Seam marker, drawn where the two halves meet. */}
      <span
        className="pointer-events-none absolute top-0 h-[68px] w-px bg-ff-ivory/70"
        style={{ left: `${((photoOnLeft ? lifestyleW : productW) / 1080) * 100}%` }}
      />
      <input
        type="range"
        aria-label="Lifestyle ratio"
        min={Math.round(LIFESTYLE_RATIO_MIN * 100)}
        max={Math.round(LIFESTYLE_RATIO_MAX * 100)}
        value={pct}
        onChange={(e) => onRatio(Number(e.target.value) / 100)}
        className="absolute inset-0 h-full w-full cursor-ew-resize opacity-0"
      />
      <p className="mt-1 text-center font-mono text-[9px] uppercase tracking-widish text-ff-mute">
        drag to resize · {lifestyleW} / {productW} px
      </p>
    </div>
  );
}

/** Miniature of the split, used on the off-state button. */
function SplitGlyph() {
  return (
    <span className="flex h-6 w-12 overflow-hidden rounded border border-ff-forest/30">
      <span className="w-1/2 bg-ff-forest/70" />
      <span className="w-1/2 bg-ff-mint" />
    </span>
  );
}
